import React, { memo } from 'react';
import classNames from 'classnames';
import { Tag, Tooltip } from 'antd';
import { useFormikContext } from 'formik';
import { EditOutlined, InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import pdmLogo from '../../../../images/logos/PDM-Brandmark-Blue.svg';
import { Analysis, AIQuickFix } from '../../../../types/analyses';
import { AnalysisType, ExtendedResources } from '../../../../types/resources';
import { Attribute } from '../../../../types/attributes';
import { SelectedListItem } from '../../../../types/item';
import AttributeNamePopover from './AttributeNamePopover';
import { intercomEvent } from '../../../utils/IntercomUtils';
import AIQuickFixHover from '../item_analysis/AIQuickFixHover';

type AttributeNameCellProps = {
  attributeId: number | string;
  rowData: Attribute;
  analyses: (Analysis & AnalysisType)[];
  selectedItems: SelectedListItem[];
  selected: boolean;
  canAccessAttributeSettings: boolean;
  aiQuickFixes?: AIQuickFix[];
  refreshSegmentData?: (segmentId: number) => void;
  handleRowSelection?: (attributeId: number | string, e: any) => void;
};

const AttributeNameCell: React.FC<AttributeNameCellProps> = ({
  attributeId,
  rowData,
  analyses,
  selectedItems,
  selected,
  canAccessAttributeSettings,
  aiQuickFixes = [],
  refreshSegmentData,
  handleRowSelection,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<any>();

  // todo: move to parent
  const handleEditAttributeName = (attributeName: string) => {
    intercomEvent('viewed-all-product', {
      action: 'item-edited',
      location: 'attribute_edit_name',
      part_number: selectedItems.map(i => i.part_number).toString(),
      brand_code: selectedItems[0]?.brand_code,
    });

    const updatedAttributes = values.attributes.map(
      (a: Attribute & { renamedAttributeId?: number }) =>
        a.uniqueId === rowData.uniqueId
          ? {
              ...rowData,
              id: null,
              name: attributeName,
              temp: true,
              renamedAttributeId: a.id || a.renamedAttributeId,
            }
          : a
    );
    setFieldValue('attributes', updatedAttributes);
  };

  const recommendationText = (rank: number | null, pdmRecommendation: number) => {
    if (rank === 1) {
      return t('attributes:recommendedByAutocare');
    }
    if (pdmRecommendation > 0) {
      return t('attributes:recommendedByPdm');
    }
    if (rank === 2) {
      return t('attributes:previouslyUsedInfo');
    }
    if (rank === 4) {
      return t('attributes:recommendedByReceiver');
    }

    return undefined;
  };

  const attributeAnalyses: (Analysis & AnalysisType)[] =
    analyses.filter(e => e.item_part_attribute?.part_attribute_id === rowData.id) || [];

  const alertTypeId = attributeAnalyses.length
    ? Math.max(...attributeAnalyses.map(e => e.alert_type_id))
    : undefined;

  const errorTooltip = (errors: (Analysis & AnalysisType)[]) => {
    const errorDescriptions = [...new Set(errors.map(e => e.description))];
    return errorDescriptions.map(e => <div>{e}</div>);
  };

  const renderedTags = canAccessAttributeSettings
    ? rowData.tags?.map(tag => <Tag key={tag}>{tag}</Tag>)
    : null;

  return (
    <div
      className={classNames('attributes__name-cell flex items-center', {
        'attributes__selected-cell': selected,
      })}
      onClick={e => !!handleRowSelection && handleRowSelection(attributeId, e)}
    >
      <Tooltip
        title={recommendationText(rowData.rank, rowData.pdm_recommendation)}
        placement="left"
      >
        <div
          className={classNames('attributes__name-cell_border', {
            'green-border': rowData.rank === 1,
            'blue-border': rowData.rank === 2 || rowData.pdm_recommendation === 1,
            'red-border': rowData.rank === 4,
          })}
        />
      </Tooltip>

      <div className="flex-1 pr-2 pl-2">{rowData.name}</div>
      {canAccessAttributeSettings && rowData.tags && rowData.tags.length > 0 && (
        <div className="attributes__tags">
          <Tooltip
            overlayClassName="attributes__tag-tooltip"
            placement="topRight"
            title={renderedTags}
            mouseEnterDelay={0.7}
          >
            <div className="attributes__tag-wrapper">{renderedTags}</div>
          </Tooltip>
        </div>
      )}
      {rowData.ranked_values.find(rv => rv.item_ids.length > 0) && (
        <AttributeNamePopover
          attributeName={rowData.name}
          handleChange={handleEditAttributeName}
          translations={rowData.translations}
        >
          <EditOutlined className="edit-button" />
        </AttributeNamePopover>
      )}

      {rowData.pdm_recommendation === 1 && (
        <img src={pdmLogo} className="pr-1 h-5" alt="pdm-logo" />
      )}
      {attributeAnalyses.length > 0 && (
        <AIQuickFixHover
          quickFixes={aiQuickFixes}
          referenceId={attributeAnalyses[0]?.reference_id || 0}
          refreshSegmentData={refreshSegmentData}
        >
          <Tooltip placement="leftTop" title={errorTooltip(attributeAnalyses)}>
            <WarningOutlined
              className={classNames('attributes__analysis-message mr-1', {
                blue: alertTypeId === 1,
                yellow: alertTypeId === 2,
                red: alertTypeId === 3,
              })}
            />
          </Tooltip>
        </AIQuickFixHover>
      )}
      {rowData.receivers.length > 0 && (
        <Tooltip
          title={
            <React.Fragment>
              <div>{t('attributes:receiversRequest')}</div>
              {rowData.receivers.map((receiver: ExtendedResources) => (
                <div key={receiver.id}>{receiver.name}</div>
              ))}
            </React.Fragment>
          }
        >
          <InfoCircleOutlined className="ant-info-tooltip attributes-star" />
        </Tooltip>
      )}
      {rowData.used_by_brand !== 0 && (
        <Tooltip title={t('attributes:previouslyUsedInfo')}>
          <Tag className="attributes__used-by-brand-tag self-center">{rowData.used_by_brand}</Tag>
        </Tooltip>
      )}
    </div>
  );
};

export default memo(AttributeNameCell);
